import { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  PackageCheck,
  Undo2,
  BarChart3,
  Users,
  Send,
  Settings,
  ChevronLeft,
  Menu,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/admin", icon: LayoutDashboard, label: "Дашборд" },
  { to: "/admin/goods", icon: Package, label: "Товары" },
  { to: "/admin/publish", icon: Send, label: "Публикация" },
  { to: "/admin/orders", icon: ShoppingCart, label: "Заказы" },
  { to: "/admin/delivery", icon: PackageCheck, label: "Выдача" },
  { to: "/admin/returns", icon: Undo2, label: "Возврат" },
  { to: "/admin/reports", icon: BarChart3, label: "Отчёты" },
  { to: "/admin/users", icon: Users, label: "Пользователи" },
  { to: "/admin/settings", icon: Settings, label: "Настройки" },
] as const;

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar — desktop */}
      <aside
        className={cn(
          "hidden md:flex flex-col border-r border-border bg-surface transition-all duration-200 sticky top-0 h-screen overflow-y-auto",
          collapsed ? "w-16" : "w-60",
        )}
      >
        <div className="flex h-14 items-center justify-between px-4 border-b border-border shrink-0">
          {!collapsed && (
            <Link to="/" className="font-bold text-lg hover:text-primary transition-colors" title="На сайт">
              Админ
            </Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-md hover:bg-gray-100 text-text-secondary"
            aria-label="Collapse sidebar"
          >
            <ChevronLeft
              className={cn(
                "h-4 w-4 transition-transform",
                collapsed && "rotate-180",
              )}
            />
          </button>
        </div>

        <nav className="flex-1 py-3 px-2 space-y-1">
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => {
            const active =
              to === "/admin"
                ? location.pathname === "/admin"
                : location.pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  active
                    ? "bg-primary-light text-primary-700"
                    : "text-text-secondary hover:bg-gray-100 hover:text-text",
                )}
                title={collapsed ? label : undefined}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="px-2 py-3 border-t border-border shrink-0">
          <Link
            to="/"
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors text-primary hover:bg-primary-light",
            )}
            title={collapsed ? "На сайт" : undefined}
          >
            <ExternalLink className="h-5 w-5 shrink-0" />
            {!collapsed && "На сайт"}
          </Link>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-surface border-r border-border animate-fade-in">
            <div className="flex h-16 items-center justify-between px-4 border-b border-border">
              <span className="font-bold text-lg">Админ</span>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu">
                <ChevronLeft className="h-5 w-5" />
              </button>
            </div>
            <nav className="py-4 px-2 space-y-1">
              {NAV_ITEMS.map(({ to, icon: Icon, label }) => {
                const active =
                  to === "/admin"
                    ? location.pathname === "/admin"
                    : location.pathname.startsWith(to);
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                      active
                        ? "bg-primary-light text-primary-700"
                        : "text-text-secondary hover:bg-gray-100",
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="px-2 py-3 border-t border-border">
              <Link
                to="/"
                className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary-light transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <ExternalLink className="h-5 w-5" />
                На сайт
              </Link>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex h-14 items-center gap-4 border-b border-border bg-surface px-4 md:px-6 sticky top-0 z-10">
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold flex-1">
            {NAV_ITEMS.find((item) =>
              item.to === "/admin"
                ? location.pathname === "/admin"
                : location.pathname.startsWith(item.to),
            )?.label ?? "Админ-панель"}
          </h1>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-lg border border-primary/20 bg-primary-light px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary hover:text-white transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            На сайт
          </Link>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
